import React from 'react';
import { Box, FormControlLabel, FormGroup } from '@mui/material';
import { DayOfWeek } from '@askporter/client-grieg-lyric';
import { RoundButton, RoundButtonSize } from '../../../../Buttons/RoundButton';
import { Checkbox } from '../../../../Checkbox';
import { Dialog } from '../../../../Dialog';
import { TimePicker } from '../../../../TimePicker';
import { DayTimeType } from '../../types';
import { defaultDays, days, getDays, mergeDayTimeState, filterNoServiceTimeDays, parseDayTime } from '../../utils';

export interface SelectDayTimeProps {
  title?: string;
  dataTestId?: string;
  t: (key: string, options?: Record<string, string | number>) => string;
  isLoading?: boolean;
  isVisible: boolean;
  handleClose: () => void;
  setIsModalVisible: (isModalVisible: boolean) => void;
  isSmallDevice: boolean;
  selectedDays?: Record<string, boolean>;
  setSelectedDays: (selectedDays: Record<string, boolean>) => void;
  onAdd: (value: Array<DayTimeType>) => void;
  value: Array<DayTimeType>;
  setTimeValue: (timeValue: string | null) => void;
  timeValue: string | null;
  noServiceCheckbox: boolean;
  setNoServiceCheckbox: (noServiceCheckbox: boolean) => void;
  addDayTime: (dayTimes: Record<DayOfWeek, string | null>) => void;
  dayTimes: Record<DayOfWeek, string | null>;
}

/**
 * Allows the user to set a time for a week day
 */
export const SelectDayTimeModal: React.FC<React.PropsWithChildren<SelectDayTimeProps>> = ({
  t,
  title,
  dataTestId,
  isLoading,
  isVisible,
  setIsModalVisible,
  handleClose,
  isSmallDevice,
  selectedDays = defaultDays,
  setSelectedDays,
  onAdd,
  value,
  setTimeValue,
  timeValue,
  noServiceCheckbox,
  setNoServiceCheckbox,
  addDayTime,
  dayTimes,
}: SelectDayTimeProps) => {
  const _onChange = (time: string | null) => {
    setTimeValue(time ? time : null);
  };

  const _onAdd = () => {
    const v = [{ days: getDays(selectedDays), time: timeValue ? timeValue : null }];
    const payload = filterNoServiceTimeDays({ ...dayTimes, ...parseDayTime(v) });

    const newState = [{ days: getDays(selectedDays), time: timeValue ? timeValue : null }];
    const mergedState = mergeDayTimeState(value || [], newState);

    onAdd(mergedState);
    addDayTime(payload);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNoServiceCheckbox(e.target.checked);
    _onChange(null);
  };

  return (
    <Dialog
      isOpen={isVisible}
      customWidth="550px"
      dialogHeaderProps={{
        title: title,
        onClose: handleClose,
        iconType: 'edit-time',
      }}
      dialogActionProps={{
        buttons: [
          {
            label: t('common:cancel'),
            variant: 'outlined',
            onClick: () => setIsModalVisible(false),
          },
          {
            label: t('common:add'),
            variant: 'contained',
            onClick: () => _onAdd(),
            disabled: (!timeValue && !noServiceCheckbox) || isLoading,
            isLoading: isLoading,
          },
        ],
      }}
    >
      <Box
        data-testid={dataTestId}
        sx={{ display: 'flex', gap: isSmallDevice ? 3 : 6.5, width: 'fit-content', margin: '0 auto' }}
      >
        {Object.keys(days).map((day, i) => (
          <RoundButton
            onClick={() => setSelectedDays({ ...selectedDays, [i]: !selectedDays[i] })}
            isSelected={selectedDays[i] === true}
            size={isSmallDevice ? RoundButtonSize.SM : RoundButtonSize.LG}
            key={`${day}-${i}`}
            aria-label={`${t(`common:day_of_week:${day}`)} - ${t(
              `day_time:edit:${selectedDays[i] === true ? 'selected' : 'unselected'}`,
            )}`}
          >
            {t(`common:day_of_week:${day}`)?.substring(0, 1)}
          </RoundButton>
        ))}
      </Box>
      <Box sx={{ mt: 6 }}>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={noServiceCheckbox} onChange={handleCheckboxChange} />}
            label={t('day_time:select_day_time:no_service')}
          />
          {!noServiceCheckbox && (
            <TimePicker
              value={timeValue}
              onChange={_onChange}
              label={t('day_time:select_day_time:time')}
              sx={{ width: '100%' }}
            />
          )}
        </FormGroup>
      </Box>
    </Dialog>
  );
};
